import Store from '@store';
import { AdvancedOrderedListChainHandler } from 'src/factories/advanced-ordered-list/chain-handler';

/**
 * Returns an object containing the elements that contain ordered list text on the current page.
 * @param {number} currentPageId - The ID of the current page.
 * @returns {Object} - An object containing the elements that contain ordered list text on the current page.
 *
 * @example
 * const currentPageId = 1;
 * const result = getElementsContainOrderedList(currentPageId);
 * // result = {
 * //   1: [{ pageId: 1, id: 'element1' }, { pageId: 1, id: 'element2' }],
 * //   2: [{ pageId: 1, id: 'element3' }]
 * // }
 */
export function getElementsContainOrderedList (currentPageId) {
  const orderedListTexts = [
    ...document.querySelectorAll('g.main [data-ordered-style]')
  ];

  const orderedStyles = [
    ...new Set(
      orderedListTexts.map((text) => Number(text.dataset.orderedStyle))
    )
  ];

  const result = {};
  for (const orderedStyle of orderedStyles) {
    if ((Number(orderedStyle)) <= 10) continue;

    const query = `g.main text[data-ordered-style="${orderedStyle}"]`;
    const elements = [
      ...new Set(
        [...document.querySelectorAll(query)].map(
          element => element.parentElement.parentElement.id
        )
      )
    ];

    result[orderedStyle] = elements.map(element => ({ pageId: currentPageId, id: element }));
  }

  return result;
}

/**
 * Returns an object containing the elements that need to be deleted from the old chains to match the new chains.
 * @param {Object} newChains - The new chains object.
 * @param {Object} oldChains - The old chains object.
 * @returns {Object|null} - An object containing the elements to delete, or null if there are no elements to delete.
 *
 * @example
 * const result = getElementsToDelete(newChains, oldChains);
 * //result = { 1: [{ id: 3, pageId: 2 }], 2: [{ id: 4, pageId: 1 }] }
 */
export function getElementsToDelete (newChains, oldChains) {
  if (!oldChains) return newChains;

  const result = {};

  for (const orderedStyle in oldChains) {
    if (!newChains[orderedStyle]) {
      result[orderedStyle] = oldChains[orderedStyle];
    } else {
      const oldElements = oldChains[orderedStyle];
      const newElements = newChains[orderedStyle];

      const elementsToDelete = oldElements.filter(
        (oldElement) => !newElements.find(newElement => newElement.id === oldElement.id && newElement.pageId === oldElement.pageId)
      );

      if (elementsToDelete.length) {
        result[orderedStyle] = elementsToDelete;
      }
    }
  }

  return Object.keys(result).length ? result : null;
}

/**
 * Returns an object containing the elements that need to be added to the old chains to match the new chains.
 * @param {Object} newChains - The new chains object.
 * @param {Object} oldChains - The old chains object.
 * @returns {Object|null} - An object containing the elements to add, or null if there are no elements to add.
 *
 * @example
 * const result = getElementsToAdd(newChains, oldChains);
 * //result = { 1: [{ id: 3, pageId: 2 }] }
 */
export function getElementsToAdd (newChains, oldChains) {
  if (!oldChains) return newChains;

  const result = {};

  for (const orderedStyle in newChains) {
    if (!oldChains[orderedStyle]) {
      result[orderedStyle] = newChains[orderedStyle];
    } else {
      const oldElements = oldChains[orderedStyle];
      const newElements = newChains[orderedStyle];

      const elementsToAdd = newElements.filter(
        (newElement) => !oldElements.find(oldElement => oldElement.id === newElement.id && oldElement.pageId === newElement.pageId)
      );

      if (elementsToAdd.length) {
        result[orderedStyle] = elementsToAdd;
      }
    }
  }

  return Object.keys(result).length ? result : null;
}

/**
 * Returns the previous element in an ordered list chain before the specified page.
 *
 * @param {Object} elementsToAdd - An object containing the elements to add to the chain, keyed by chain ID.
 * @param {number} pageId - The ID of the page to find the previous element before.
 * @returns {Object} An object containing the previous element for each chain, keyed by chain ID.
 */
export function getPreviousElementInChain (elementsToAdd, pageId) {
  const pagesOrder = Store.getState().editor.pagesOrder;
  const allChains = new AdvancedOrderedListChainHandler().extractChains();

  const pagesBeforeCurrentPage = pagesOrder.slice(0, pagesOrder.indexOf(pageId)).reverse();

  const result = {};
  for (const chainId in elementsToAdd) {
    const chain = allChains[chainId];
    if (!chain) continue;

    for (const page of pagesBeforeCurrentPage) {
      const element = chain.slice().reverse().find(item => item.pageId === page);
      if (element) {
        result[chainId] = element;
        break;
      }
    }
  }

  return result;
}
